import React, { Dispatch, FC, SetStateAction, useEffect, useMemo } from 'react'
import { Button } from 'components'
import { PackageDiscountPriceBar } from './PackageDiscountPriceBar'
import { PackageDiscount, SelectedPacket } from './enrollFormTypes'
import {
    constants,
    generateUuidv4,
    getOwnedPackageCount,
    getPackageDiscountAmount,
    numberFormatter
} from 'helpers'
import { Order } from 'storeTypes'
import './packetQuantityView.scss'

const MAX_HERO_TILES = 6

type PacketQuantityViewProps = {
    productPacket: SelectedPacket
    discounts?: PackageDiscount
    selectedPackets: SelectedPacket[]
    onPacketSelection: Dispatch<SetStateAction<SelectedPacket[]>>
    fullGoalItems: Order[]
    showDiscountBar?: boolean
    autoSeed?: boolean
    onContinue: () => void
    continueLabel: string
    onBack?: () => void
    onSizePress?: () => void
}

export const PacketQuantityView: FC<PacketQuantityViewProps> = ({
    productPacket,
    discounts,
    selectedPackets,
    onPacketSelection,
    fullGoalItems,
    showDiscountBar = true,
    autoSeed = true,
    onContinue,
    continueLabel,
    onBack,
    onSizePress
}) => {
    const packageId = productPacket.package_id
    const listPrice = Number(productPacket.price) || 0
    const hidePrice = Boolean(productPacket.hidePrice)

    const cartQty = useMemo(
        () =>
            selectedPackets.filter((packet) => packet.package_id === packageId)
                .length,
        [selectedPackets, packageId]
    )

    const owned = useMemo(
        () =>
            getOwnedPackageCount(packageId, {
                fullGoalItems
            }),
        [packageId, fullGoalItems]
    )

    const discount = showDiscountBar
        ? getPackageDiscountAmount(discounts, cartQty, owned)
        : 0
    const listTotal = listPrice * cartQty
    const netTotal = listTotal - discount

    // Seed 1 packet when autoSeed is enabled and the cart is empty for this
    // size. For multi-size products, autoSeed is false until the agent picks a
    // size. Re-visiting a zeroed size (size switch or Back → remount) re-seeds
    // to 1 by product choice: an empty cart for the active size always starts
    // at qty 1.
    useEffect(() => {
        if (autoSeed && cartQty === 0) {
            onPacketSelection((prev) => [
                ...(prev || []),
                { ...productPacket, uuid: generateUuidv4() }
            ])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packageId, autoSeed])

    const setCartQty = (nextQty: number) => {
        const clamped = Math.max(
            0,
            Math.min(nextQty, constants.MAX_ORDER_OF_PACKAGE)
        )
        onPacketSelection((prevPackets) => {
            const others = (prevPackets || []).filter(
                (packet) => packet.package_id !== packageId
            )
            const existingForPackage = (prevPackets || []).filter(
                (packet) => packet.package_id === packageId
            )
            const nextForPackage: SelectedPacket[] = []
            for (let i = 0; i < clamped; i += 1) {
                nextForPackage.push(
                    existingForPackage[i] || {
                        ...productPacket,
                        uuid: generateUuidv4()
                    }
                )
            }
            return [...others, ...nextForPackage]
        })
    }

    const displayName = productPacket.name?.trim() || ''

    const visibleImageCount =
        cartQty > MAX_HERO_TILES ? MAX_HERO_TILES - 1 : Math.max(cartQty, 1)
    const hiddenImageCount = Math.max(0, cartQty - visibleImageCount)
    const heroTileCount = visibleImageCount + (hiddenImageCount > 0 ? 1 : 0)

    return (
        <div
            className="packet-quantity-view"
            data-testid="packet-quantity-view"
        >
            <div className="packet-quantity-view__summary">
                {productPacket.image_url && (
                    <img
                        src={productPacket.image_url}
                        alt=""
                        className="packet-quantity-view__summary-thumb"
                    />
                )}
                <div className="packet-quantity-view__summary-text-group">
                    <span
                        className="packet-quantity-view__summary-name"
                        title={displayName}
                    >
                        {displayName}
                    </span>
                    {onSizePress ? (
                        <button
                            className="packet-quantity-view__summary-size packet-quantity-view__summary-size--tappable"
                            onClick={onSizePress}
                            aria-label="Change package size"
                        >
                            {productPacket.size_display} ›
                        </button>
                    ) : (
                        <span className="packet-quantity-view__summary-size">
                            {productPacket.size_display}
                        </span>
                    )}
                    <span
                        className="packet-quantity-view__summary-price"
                        data-testid="packet-quantity-unit-price"
                    >
                        {numberFormatter(listPrice, 'CFA', hidePrice)}
                    </span>
                </div>
            </div>

            <div className="packet-quantity-view__body">
                {productPacket.image_url && (
                    <div
                        className="packet-quantity-view__hero"
                        data-tiles={heroTileCount}
                        data-testid="packet-quantity-hero"
                    >
                        {Array.from({ length: visibleImageCount }).map(
                            (_, index) => (
                                <img
                                    key={`${packageId}-hero-${index}`}
                                    src={productPacket.image_url}
                                    alt={index === 0 ? displayName : ''}
                                    aria-hidden={index === 0 ? undefined : true}
                                    className="packet-quantity-view__hero-image"
                                />
                            )
                        )}
                        {hiddenImageCount > 0 && (
                            <span
                                className="packet-quantity-view__hero-overflow"
                                data-testid="packet-quantity-hero-overflow"
                            >
                                {`+${hiddenImageCount}`}
                            </span>
                        )}
                    </div>
                )}

                <div className="packet-quantity-view__stepper">
                    <button
                        type="button"
                        className="packet-quantity-view__stepper-btn"
                        data-testid="packet-quantity-decrement"
                        aria-label="decrease-quantity"
                        disabled={cartQty <= 0}
                        onClick={() => setCartQty(cartQty - 1)}
                    >
                        −
                    </button>
                    <span
                        key={cartQty}
                        className="packet-quantity-view__stepper-count"
                        data-testid="packet-quantity-count"
                    >
                        {cartQty}
                    </span>
                    <button
                        type="button"
                        className="packet-quantity-view__stepper-btn packet-quantity-view__stepper-btn--plus"
                        data-testid="packet-quantity-increment"
                        aria-label="increase-quantity"
                        disabled={cartQty >= constants.MAX_ORDER_OF_PACKAGE}
                        onClick={() => setCartQty(cartQty + 1)}
                    >
                        +
                    </button>
                </div>

                {showDiscountBar ? (
                    <PackageDiscountPriceBar
                        listTotal={listTotal}
                        discount={discount}
                        netTotal={netTotal}
                        hidePrice={hidePrice}
                    />
                ) : (
                    <div
                        className="packet-quantity-view__list-total"
                        data-testid="packet-quantity-list-total"
                    >
                        {numberFormatter(listTotal, 'CFA', hidePrice)}
                    </div>
                )}
            </div>

            <div className="packet-quantity-view__footer">
                {onBack && (
                    <Button
                        className="packet-quantity-view__back"
                        style="secondary"
                        size="tall"
                        onClick={onBack}
                    >
                        ‹ Back
                    </Button>
                )}
                <Button
                    className="packet-quantity-view__continue"
                    style="primary"
                    size="tall"
                    onClick={onContinue}
                >
                    {continueLabel}
                </Button>
            </div>
        </div>
    )
}
